'use client';
import React, { useState, useRef } from 'react';
import { Upload, FileText, X, CheckCircle, Loader, Ship, ArrowLeft, PlusCircle, RefreshCw, Archive, Check, AlertTriangle, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import PushedOverlay from '@/components/ui/PushedOverlay';
import { useCertificates } from '@/contexts/CertificatesContext';
import { useSeafarerProfile } from '@/contexts/SeafarerProfileContext';
import { useAuth } from '@/contexts/AuthContext';
import { isNativeRuntime } from '@/lib/native/platform';
import { buildImportPlan, type ImportPlan } from '@/lib/dg-profile/import-plan';
import { HelpTipList, HelpTip } from '@/components/HelpTip';
import type { FullSeafarerData } from '@/lib/types/seafarer';
import { buildExistingProfileContext } from '@/lib/dg-profile/context-builder';

const IMPORT_TIPS = [
  'A DG Shipping profile PDF gives the most accurate results — choose it over a CV whenever you have one.',
  'Use a clear, text-based PDF (not a blurry photo or scan) so every record is read correctly.',
  'Already imported once? You can edit any certificate or voyage directly later — no need to re-upload each time.',
];

const MAX_PROFILE_PDF_BYTES = 8 * 1024 * 1024;
const LIVE_API_ORIGIN = 'https://seamigo.vesselos.in';

interface UploadBannerProps {
  compact?: boolean;
  /** 'card' renders a Seamigo quick-action tile (icon + title + sub) that opens the same import modal. */
  variant?: 'button' | 'card';
}

type UploadState = 'idle' | 'dragging' | 'uploading' | 'parsing' | 'reviewing' | 'success' | 'error';
type PdfImportType = 'dg-profile' | 'maritime-cv';

const PARSE_POLL_TIMEOUT_MS = 12 * 60 * 1000;

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function readJsonResponse(response: Response) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { error: text };
  }
}

function apiPath(path: string) {
  if (typeof window === 'undefined') return path;
  const host = window.location.hostname;
  const isLocalWeb = host === 'localhost' || host === '127.0.0.1';
  if (!isNativeRuntime() && isLocalWeb) return path;
  return `${LIVE_API_ORIGIN}${path}`;
}

const STEP_LABELS = ['Type', 'Upload', 'Review', 'Done'];

/** Maps the elapsed-progress signal to an honest, staged status message. */
function parseStageMessage(progress: number): string {
  if (progress < 30) return 'Reading your document…';
  if (progress < 60) return 'Extracting certificates, documents, and sea service…';
  if (progress < 85) return 'Matching against your existing records…';
  return 'Almost there…';
}

function ImportStepper({ current }: { current: number }) {
  return (
    <div className="flex items-center gap-1.5" aria-label={`Step ${current + 1} of ${STEP_LABELS.length}`}>
      {STEP_LABELS.map((label, i) => {
        const state = i < current ? 'done' : i === current ? 'active' : 'todo';
        return (
          <React.Fragment key={label}>
            <div className="flex items-center gap-1.5">
              <span
                className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-700 transition-colors ${
                  state === 'done'
                    ? 'bg-primary text-white'
                    : state === 'active'
                      ? 'bg-accent text-white'
                      : 'bg-muted text-muted-foreground'
                }`}
              >
                {state === 'done' ? <Check size={11} /> : i + 1}
              </span>
              <span
                className={`text-[11px] font-600 ${state === 'todo' ? 'text-muted-foreground' : 'text-foreground'} ${
                  state === 'active' ? '' : 'hidden sm:inline'
                }`}
              >
                {label}
              </span>
            </div>
            {i < STEP_LABELS.length - 1 && <span className="h-px flex-1 bg-border" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function UploadBanner({ compact = false, variant = 'button' }: UploadBannerProps) {
  const { replaceCerts } = useCertificates();
  const { data: storedProfileData, saveData } = useSeafarerProfile();
  const { getAccessToken, isConfigured } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [fileName, setFileName] = useState('');
  const [parseProgress, setParseProgress] = useState(0);
  const [parsedCount, setParsedCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [importType, setImportType] = useState<PdfImportType | null>(null);
  const [plan, setPlan] = useState<ImportPlan | null>(null);
  const [applying, setApplying] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isUpdate = !!storedProfileData;

  const uploadAndParse = async (file: File, selectedImportType: PdfImportType) => {
    setFileName(file.name);
    setUploadState('uploading');
    setErrorMessage('');

    // Simulate upload progress briefly
    await new Promise((r) => setTimeout(r, 400));
    setUploadState('parsing');

    // Animate progress while waiting for API
    let progress = 0;
    const progressInterval = setInterval(() => {
      progress = Math.min(progress + (progress < 85 ? 5 : 1), 95);
      setParseProgress(progress);
    }, 1000);

    const pollParseJob = async (jobId: string, token: string | null) => {
      const startedAt = Date.now();

      while (Date.now() - startedAt < PARSE_POLL_TIMEOUT_MS) {
        await sleep(2500);

        const statusResponse = await fetch(apiPath(`/api/parse-dg-profile?jobId=${encodeURIComponent(jobId)}`), {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        const statusData = await readJsonResponse(statusResponse);

        if (!statusResponse.ok) {
          throw new Error(statusData.error || 'Could not check PDF parsing status.');
        }

        if (statusData.status === 'completed' && statusData.result) {
          return statusData.result;
        }

        if (statusData.status === 'failed') {
          throw new Error(statusData.error || 'Could not read this maritime profile or CV PDF. Please try again.');
        }
      }

      throw new Error('PDF import is taking longer than expected. Please try again in a few minutes.');
    };

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('importType', selectedImportType);
      // Send compact existing-profile summary so the AI can flag discrepancies
      if (storedProfileData) {
        try {
          formData.append('existingContext', buildExistingProfileContext(storedProfileData));
        } catch {
          // best-effort — never block upload
        }
      }
      const token = isConfigured ? await getAccessToken() : null;

      const response = await fetch(apiPath('/api/parse-dg-profile'), {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: formData,
      });

      if (!response.ok) {
        const errData = await readJsonResponse(response);
        throw new Error(errData.error || 'Failed to import PDF');
      }

      const startData = await readJsonResponse(response);
      const data = startData.jobId ? await pollParseJob(startData.jobId, token) : (startData.result ?? startData);

      clearInterval(progressInterval);
      setParseProgress(100);

      if (!data.profile) {
        throw new Error('No readable records were found in this PDF. Please upload a clearer DG profile or maritime CV.');
      }

      // Compute a reviewable plan WITHOUT writing anything. The user confirms
      // before any change is applied; the merged result previewed here is the
      // exact object persisted on confirm.
      const incoming: FullSeafarerData = {
        profile: data.profile,
        education: data.education ?? [],
        preSeaTraining: data.preSeaTraining ?? [],
        modularCourses: data.modularCourses ?? [],
        seaGoingService: data.seaGoingService ?? [],
        postSeaTraining: data.postSeaTraining ?? [],
        certificatesOfEndorsement: data.certificatesOfEndorsement ?? [],
        certificatesOfCompetency: data.certificatesOfCompetency ?? [],
        authorisedDocuments: data.authorisedDocuments ?? [],
      };
      const importPlan = buildImportPlan(
        storedProfileData ?? null,
        incoming,
        selectedImportType,
        undefined,
        data.discrepancies ?? [],
      );
      setPlan(importPlan);
      setUploadState('reviewing');
    } catch (err) {
      clearInterval(progressInterval);
      setErrorMessage(err instanceof Error ? err.message : 'An unexpected error occurred');
      setUploadState('error');
    }
  };

  const applyPlan = async () => {
    if (!plan || applying) return;
    setApplying(true);
    try {
      try {
        await saveData(plan.merged);
      } catch (saveError) {
        console.error('PDF profile data imported locally but cloud sync is pending:', saveError);
        toast.warning('PDF data imported on this device. Cloud sync will retry when the account database is available.');
      }
      await replaceCerts(plan.certificates, fileName);
      setParsedCount(plan.certificates.length);
      toast.success(`${isUpdate ? 'Updated' : 'Imported'} ${plan.certificates.length} certificates from your PDF`);
      setUploadState('success');
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Could not apply the import. Please try again.');
      setUploadState('error');
    } finally {
      setApplying(false);
    }
  };

  const handleFile = (file: File) => {
    if (!importType) {
      setErrorMessage('Choose DG Profile or Maritime CV before selecting a file.');
      setUploadState('error');
      return;
    }
    const isPdf = file.type === 'application/pdf';
    const isDocx = file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      || file.name.toLowerCase().endsWith('.docx');
    if (!isPdf && !isDocx) {
      setErrorMessage('Only PDF and DOCX files are accepted. Upload a DG profile PDF or maritime CV PDF/DOCX.');
      setUploadState('error');
      return;
    }
    if (file.size > MAX_PROFILE_PDF_BYTES) {
      setErrorMessage('File is too large. Please upload a file smaller than 8 MB.');
      setUploadState('error');
      return;
    }
    uploadAndParse(file, importType);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setUploadState('idle');
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const resetUpload = () => {
    setUploadState('idle');
    setFileName('');
    setParseProgress(0);
    setParsedCount(0);
    setErrorMessage('');
    setImportType(null);
    setPlan(null);
    setApplying(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  if (variant === 'card') {
    return (
      <>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="quick-action-card"
        >
          <span className="quick-action-icon" style={{ backgroundColor: 'var(--status-verify-bg)', color: 'var(--status-verify)' }}>
            <Upload size={18} />
          </span>
          <span className="quick-action-title">Import profile</span>
          <span className="quick-action-sub">DG profile or CV</span>
        </button>
        <UploadModal
          open={modalOpen}
          onClose={() => { setModalOpen(false); resetUpload(); }}
          uploadState={uploadState}
          fileName={fileName}
          parseProgress={parseProgress}
          parsedCount={parsedCount}
          errorMessage={errorMessage}
          onDrop={handleDrop}
          onFileInput={handleFileInput}
          fileInputRef={fileInputRef}
          onReset={resetUpload}
          isUpdate={isUpdate}
          importType={importType}
          onImportTypeChange={setImportType}
          plan={plan}
          applying={applying}
          onConfirm={applyPlan}
          onCancel={resetUpload}
        />
      </>
    );
  }

  if (compact) {
    return (
      <>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="btn-primary flex items-center justify-center gap-2 whitespace-nowrap shrink-0"
        >
          <Upload size={16} />
          <span className="hidden sm:inline">Import Profile or CV PDF</span>
          <span className="sm:hidden">Import PDF</span>
        </button>
        <UploadModal
          open={modalOpen}
          onClose={() => { setModalOpen(false); resetUpload(); }}
          uploadState={uploadState}
          fileName={fileName}
          parseProgress={parseProgress}
          parsedCount={parsedCount}
          errorMessage={errorMessage}
          onDrop={handleDrop}
          onFileInput={handleFileInput}
          fileInputRef={fileInputRef}
          onReset={resetUpload}
          isUpdate={isUpdate}
          importType={importType}
          onImportTypeChange={setImportType}
          plan={plan}
          applying={applying}
          onConfirm={applyPlan}
          onCancel={resetUpload}
        />
      </>
    );
  }

  return (
    <>
      <div className="flex flex-col items-center gap-3">
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="btn-primary inline-flex items-center justify-center gap-2"
        >
          <Upload size={16} />
          Import Profile or CV PDF
        </button>
        {isUpdate ? (
          <p className="text-xs text-muted-foreground text-center">
            Import a newer DG profile or maritime CV. Existing manual and corrected records are preserved.
          </p>
        ) : null}
      </div>
      <UploadModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); resetUpload(); }}
        uploadState={uploadState}
        fileName={fileName}
        parseProgress={parseProgress}
        parsedCount={parsedCount}
        errorMessage={errorMessage}
        onDrop={handleDrop}
        onFileInput={handleFileInput}
        fileInputRef={fileInputRef}
        onReset={resetUpload}
        isUpdate={isUpdate}
        importType={importType}
        onImportTypeChange={setImportType}
        plan={plan}
        applying={applying}
        onConfirm={applyPlan}
        onCancel={resetUpload}
      />
    </>
  );
}

interface UploadModalProps {
  open: boolean;
  onClose: () => void;
  uploadState: UploadState;
  fileName: string;
  parseProgress: number;
  parsedCount: number;
  errorMessage: string;
  onDrop: (e: React.DragEvent) => void;
  onFileInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onReset: () => void;
  isUpdate: boolean;
  importType: PdfImportType | null;
  onImportTypeChange: (type: PdfImportType | null) => void;
  plan: ImportPlan | null;
  applying: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function UploadModal({
  open, onClose, uploadState, fileName, parseProgress, parsedCount, errorMessage,
  onDrop, onFileInput, fileInputRef, onReset, isUpdate, importType, onImportTypeChange,
  plan, applying, onConfirm, onCancel,
}: UploadModalProps) {
  const currentStep =
    uploadState === 'success' ? 3
    : uploadState === 'reviewing' || uploadState === 'parsing' ? 2
    : uploadState === 'uploading' ? 1
    : importType ? 1 : 0;

  if (!open) return null;

  return (
    <PushedOverlay open onClose={onClose} title="Import profile">
      <div className="space-y-3 sm:space-y-4">
        {uploadState !== 'error' && <ImportStepper current={currentStep} />}
        {uploadState === 'idle' || uploadState === 'dragging' ? (
          <div className="space-y-3">
            {!importType ? (
              <>
                <p className="text-sm font-700 text-foreground">What are you importing?</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => onImportTypeChange('dg-profile')}
                    className="card-hover rounded-lg border border-border bg-card p-4 text-left hover:border-primary hover:bg-primary/5"
                  >
                    <Ship size={22} className="text-primary mb-3" />
                    <span className="block text-sm font-700 text-foreground">DG Profile</span>
                    <span className="block text-xs text-muted-foreground mt-1">Uses the dedicated DG Shipping profile parser.</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => onImportTypeChange('maritime-cv')}
                    className="card-hover rounded-lg border border-border bg-card p-4 text-left hover:border-primary hover:bg-primary/5"
                  >
                    <FileText size={22} className="text-primary mb-3" />
                    <span className="block text-sm font-700 text-foreground">Maritime CV</span>
                    <span className="block text-xs text-muted-foreground mt-1">Reads existing CV tables and maritime records.</span>
                  </button>
                </div>
                <HelpTipList tips={IMPORT_TIPS} />
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => onImportTypeChange(null)}
                  className="inline-flex items-center gap-1 text-xs font-600 text-primary hover:opacity-70"
                >
                  <ArrowLeft size={14} />
                  Change document type
                </button>
                <div className="rounded-lg border border-border bg-muted/50 p-3">
                  <label htmlFor="profile-pdf-file" className="block text-sm font-700 text-foreground mb-2">
                    Choose {importType === 'dg-profile' ? 'DG Profile PDF' : 'Maritime CV (PDF or DOCX)'}
                  </label>
                  <input
                    id="profile-pdf-file"
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf,.pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={onFileInput}
                    aria-label={`Import ${importType === 'dg-profile' ? 'DG Profile' : 'Maritime CV'} PDF`}
                    className="block w-full text-sm text-foreground file:mr-3 file:rounded-lg file:border-0 file:bg-primary file:px-4 file:py-2.5 file:text-sm file:font-700 file:text-white file:shadow-none"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Existing manually added and corrected records are preserved.
                  </p>
                </div>
                <HelpTip>
                  {importType === 'dg-profile'
                    ? 'Tip: a freshly downloaded DG profile gives the most complete, up-to-date result.'
                    : 'Tip: a DG Shipping profile reads more accurately than a CV — use one if you have it.'}
                </HelpTip>

                <div
                  className={`upload-zone hidden sm:block p-8 text-center ${uploadState === 'dragging' ? 'drag-over' : ''}`}
                  onDrop={onDrop}
                  onDragOver={(e) => { e.preventDefault(); }}
                  onDragEnter={() => {}}
                >
                  <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center mx-auto mb-3">
                    <FileText size={24} className="text-primary" />
                  </div>
                  <p className="text-sm font-600 text-foreground mb-1">
                    Drop your {importType === 'dg-profile' ? 'DG Profile PDF' : 'Maritime CV PDF or DOCX'} here
                  </p>
                  <p className="text-xs text-muted-foreground">{importType === 'dg-profile' ? 'PDF only' : 'PDF or DOCX'}</p>
                </div>
              </>
            )}
          </div>
        ) : uploadState === 'uploading' ? (
          <div className="p-6 text-center">
            <Loader size={32} className="animate-spin text-primary mx-auto mb-3" />
            <p className="text-sm font-600 text-foreground">Uploading {fileName}...</p>
            <p className="text-xs text-muted-foreground mt-1">Please wait</p>
          </div>
        ) : uploadState === 'parsing' ? (
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                <FileText size={20} className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-600 text-foreground truncate">{fileName}</p>
                <p className="text-xs text-muted-foreground">
                  Reading your {importType === 'dg-profile' ? 'DG Profile' : 'Maritime CV'}...
                </p>
              </div>
            </div>
            <div className="progress-indeterminate w-full bg-muted rounded-full h-2" />
            <p className="text-xs font-600 text-foreground mt-3">{parseStageMessage(parseProgress)}</p>
            <p className="text-xs text-muted-foreground mt-1">
              Large PDFs can take a few minutes — you can keep this open.
            </p>
          </div>
        ) : uploadState === 'reviewing' && plan ? (
          <ImportReview
            plan={plan}
            isUpdate={isUpdate}
            applying={applying}
            onConfirm={onConfirm}
            onCancel={onCancel}
          />
        ) : uploadState === 'success' ? (
          <div className="p-6 text-center fade-in">
            <div className="pop-in w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: 'var(--status-valid-bg)' }}>
              <CheckCircle size={28} style={{ color: 'var(--status-valid)' }} />
            </div>
            <p className="text-base font-700 text-foreground mb-1">
              {isUpdate ? 'Profile Updated Successfully' : 'Profile Imported Successfully'}
            </p>
            <p className="text-sm text-muted-foreground mb-2">
              {parsedCount > 0
                ? `${parsedCount} certificate${parsedCount !== 1 ? 's' : ''} extracted from your PDF.`
                : 'No certificates found in the uploaded PDF.'}
            </p>
            {parsedCount > 0 && (
              <p className="text-xs text-muted-foreground">Review the table below to see your certificates.</p>
            )}
            <div className="flex gap-2 mt-5">
              <button type="button" onClick={onReset} className="flex-1 py-2.5 text-sm font-600 text-muted-foreground border border-border rounded-lg hover:bg-muted transition-colors">
                Upload Another PDF
              </button>
              <button type="button" onClick={onClose} className="btn-primary flex-1 py-2.5 text-sm">
                View Certificates
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 text-center">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: 'var(--status-critical-bg)' }}>
              <X size={28} style={{ color: 'var(--status-critical)' }} />
            </div>
            <p className="text-base font-700 text-foreground mb-1">Upload Failed</p>
            <p className="text-sm text-muted-foreground mb-4">
              {errorMessage || 'Only PDF files are accepted. Upload a DG profile or maritime CV PDF.'}
            </p>
            <button type="button" onClick={onReset} className="btn-primary px-6">Try Again</button>
          </div>
        )}

        {(uploadState === 'idle' || uploadState === 'dragging') && (
          <div className="text-xs text-muted-foreground bg-muted/60 rounded-lg px-3 py-2 leading-relaxed max-sm:hidden">
            {isUpdate
              ? 'Your dashboard, profile, and CV builder will merge available records while preserving manual corrections.'
              : 'Choose the matching document type so Seamigo uses the correct parser.'}
          </div>
        )}
      </div>
    </PushedOverlay>
  );
}

const CHANGE_STYLE: Record<'added' | 'updated' | 'archived', { label: string; color: string; bg: string; Icon: typeof PlusCircle }> = {
  added:    { label: 'New',      color: 'var(--status-valid)',    bg: 'var(--status-valid-bg)',    Icon: PlusCircle },
  updated:  { label: 'Updated',  color: 'var(--accent)',          bg: 'var(--secondary)',          Icon: RefreshCw },
  archived: { label: 'Archived', color: 'var(--muted-foreground)', bg: 'var(--muted)',             Icon: Archive },
};

function SummaryStat({ kind, count }: { kind: 'added' | 'updated' | 'archived'; count: number }) {
  const style = CHANGE_STYLE[kind];
  return (
    <div className="flex items-center gap-1.5">
      <span className="inline-flex h-2 w-2 rounded-full" style={{ backgroundColor: style.color }} />
      <span className="font-700 text-foreground font-tabular">{count}</span>
      <span className="text-muted-foreground">{style.label.toLowerCase()}</span>
    </div>
  );
}

function ImportReview({
  plan, isUpdate, applying, onConfirm, onCancel,
}: {
  plan: ImportPlan;
  isUpdate: boolean;
  applying: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { totals } = plan;
  const certDelta = plan.certificatesAfter - plan.certificatesBefore;
  const noChanges = totals.added === 0 && totals.updated === 0 && totals.archived === 0;

  return (
    <div className="space-y-3">
      <div>
        <p className="text-base font-700 text-foreground">{isUpdate ? 'Review changes' : 'Review import'}</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {isUpdate
            ? 'Nothing is saved until you apply. Existing manual and corrected records are preserved.'
            : 'Confirm the records found in your PDF before importing.'}
        </p>
      </div>

      {/* Summary band */}
      <div className="rounded-xl border border-border bg-muted/40 px-4 py-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
          <SummaryStat kind="added" count={totals.added} />
          <SummaryStat kind="updated" count={totals.updated} />
          {totals.archived > 0 && <SummaryStat kind="archived" count={totals.archived} />}
          {totals.unchanged > 0 && (
            <span className="text-xs text-muted-foreground">{totals.unchanged} unchanged</span>
          )}
        </div>
        <div className="mt-2 pt-2 border-t border-border text-xs text-muted-foreground">
          <span className="font-700 text-foreground font-tabular">{plan.certificatesAfter}</span> certificate
          {plan.certificatesAfter !== 1 ? 's' : ''} after import
          {isUpdate && certDelta !== 0 && (
            <span className="ml-1" style={{ color: certDelta > 0 ? 'var(--status-valid)' : 'var(--muted-foreground)' }}>
              ({certDelta > 0 ? '+' : ''}{certDelta})
            </span>
          )}
        </div>
      </div>

      {noChanges ? (
        <div className="rounded-lg border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
          This PDF matches what you already have — no new or changed records were found.
        </div>
      ) : (
        <div className="space-y-2 max-h-[42vh] overflow-y-auto overscroll-contain pr-0.5">
          {plan.sections
            .filter(section => section.added + section.updated + section.archived > 0)
            .map(section => (
              <div key={section.section} className="rounded-lg border border-border bg-card">
                <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                  <span className="text-sm font-700 text-foreground">{section.label}</span>
                  <span className="flex items-center gap-2 text-[11px] font-600">
                    {section.added > 0 && <span style={{ color: 'var(--status-valid)' }}>+{section.added}</span>}
                    {section.updated > 0 && <span style={{ color: 'var(--accent)' }}>~{section.updated}</span>}
                    {section.archived > 0 && <span className="text-muted-foreground">⌧{section.archived}</span>}
                  </span>
                </div>
                <ul className="divide-y divide-border">
                  {section.items.map((item, idx) => {
                    const style = CHANGE_STYLE[item.kind];
                    const Icon = style.Icon;
                    return (
                      <li key={idx} className="flex items-center gap-2.5 px-3 py-2">
                        <span
                          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                          style={{ backgroundColor: style.bg, color: style.color }}
                        >
                          <Icon size={13} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-xs font-600 text-foreground">{item.title}</span>
                          {item.detail && <span className="block truncate text-[11px] text-muted-foreground">{item.detail}</span>}
                        </span>
                      </li>
                    );
                  })}
                  {section.added + section.updated + section.archived > section.items.length && (
                    <li className="px-3 py-1.5 text-[11px] text-muted-foreground">
                      +{section.added + section.updated + section.archived - section.items.length} more
                    </li>
                  )}
                </ul>
              </div>
            ))}
        </div>
      )}

      {/* AI discrepancies — flagged by the parser when existing profile context was provided */}
      {plan.aiDiscrepancies.length > 0 && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 space-y-1.5">
          <div className="flex items-center gap-1.5">
            <AlertTriangle size={14} className="text-amber-500 shrink-0" />
            <span className="text-xs font-700 text-amber-600 dark:text-amber-400">
              Discrepancies detected by AI ({plan.aiDiscrepancies.length})
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground">
            The document contains values that differ from your existing records. Review before applying.
          </p>
          <ul className="space-y-1">
            {plan.aiDiscrepancies.slice(0, 4).map((d, idx) => (
              <li key={idx} className="text-[11px]">
                <span className="font-600 text-foreground">{d.field}: </span>
                <span className="text-muted-foreground">on file </span>
                <span className="font-600 text-foreground">{d.existingValue}</span>
                <span className="text-muted-foreground"> → PDF says </span>
                <span className="font-600 text-foreground">{d.incomingValue}</span>
                {d.note && <span className="text-muted-foreground"> — {d.note}</span>}
              </li>
            ))}
            {plan.aiDiscrepancies.length > 4 && (
              <li className="text-[11px] text-muted-foreground">+{plan.aiDiscrepancies.length - 4} more</li>
            )}
          </ul>
        </div>
      )}

      {/* Cert-number conflicts — same canonical cert but different numbers between existing and incoming */}
      {plan.certConflicts.length > 0 && (
        <div className="rounded-lg border border-orange-500/40 bg-orange-500/10 px-3 py-2.5 space-y-1.5">
          <div className="flex items-center gap-1.5">
            <AlertTriangle size={14} className="text-orange-500 shrink-0" />
            <span className="text-xs font-700 text-orange-600 dark:text-orange-400">
              Certificate number mismatch ({plan.certConflicts.length})
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Existing records are kept. Check whether these are renewals or data errors.
          </p>
          <ul className="space-y-1">
            {plan.certConflicts.slice(0, 3).map((c, idx) => (
              <li key={idx} className="text-[11px]">
                <span className="font-600 text-foreground">{c.label} </span>
                <span className="text-muted-foreground">({c.section}): on file </span>
                <span className="font-600 text-foreground">{c.existingValue}</span>
                <span className="text-muted-foreground"> / PDF says </span>
                <span className="font-600 text-foreground">{c.incomingValue}</span>
              </li>
            ))}
            {plan.certConflicts.length > 3 && (
              <li className="text-[11px] text-muted-foreground">+{plan.certConflicts.length - 3} more</li>
            )}
          </ul>
        </div>
      )}

      {/* Overlap warning — shown when two sea-service entries have overlapping dates */}
      {plan.seaServiceOverlaps.length > 0 && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 space-y-1.5">
          <div className="flex items-center gap-1.5">
            <AlertTriangle size={14} className="text-amber-500 shrink-0" />
            <span className="text-xs font-700 text-amber-600 dark:text-amber-400">
              Overlapping sea service ({plan.seaServiceOverlaps.length} conflict{plan.seaServiceOverlaps.length !== 1 ? 's' : ''})
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground">
            A seafarer cannot sail on two ships at the same time. Check these entries after importing and correct the sign-on / sign-off dates.
          </p>
          <ul className="space-y-1">
            {plan.seaServiceOverlaps.slice(0, 3).map((ov, idx) => (
              <li key={idx} className="text-[11px] text-foreground">
                <span className="font-600">{ov.aShip}</span>
                {' '}({ov.aFrom}–{ov.aOngoing ? 'ongoing' : ov.aTo}){' '}
                <span className="text-muted-foreground">overlaps</span>{' '}
                <span className="font-600">{ov.bShip}</span>
                {' '}({ov.bFrom}–{ov.bOngoing ? 'ongoing' : ov.bTo}){' '}
                <span className="text-muted-foreground">
                  {(ov.aOngoing || ov.bOngoing) ? '— verify active assignment' : `by ${ov.overlapDays} day${ov.overlapDays !== 1 ? 's' : ''}`}
                </span>
              </li>
            ))}
            {plan.seaServiceOverlaps.length > 3 && (
              <li className="text-[11px] text-muted-foreground">
                +{plan.seaServiceOverlaps.length - 3} more conflict{plan.seaServiceOverlaps.length - 3 !== 1 ? 's' : ''}
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Sea service date errors — reversed dates, future dates, unparseable */}
      {plan.seaServiceDateErrors.length > 0 && (
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2.5 space-y-1.5">
          <div className="flex items-center gap-1.5">
            <XCircle size={14} className="text-red-500 shrink-0" />
            <span className="text-xs font-700 text-red-600 dark:text-red-400">
              Sea service date errors ({plan.seaServiceDateErrors.length})
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground">
            These entries have invalid sign-on / sign-off dates. Correct them after importing.
          </p>
          <ul className="space-y-1">
            {plan.seaServiceDateErrors.slice(0, 3).map((e, idx) => (
              <li key={idx} className="text-[11px] text-foreground">
                <span className="font-600">{e.shipName}: </span>
                <span className="text-muted-foreground">{e.note}</span>
              </li>
            ))}
            {plan.seaServiceDateErrors.length > 3 && (
              <li className="text-[11px] text-muted-foreground">+{plan.seaServiceDateErrors.length - 3} more</li>
            )}
          </ul>
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={applying}
          className="flex-1 py-2.5 text-sm font-600 text-muted-foreground border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={applying}
          className="btn-primary flex-1 py-2.5 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-70"
        >
          {applying && <Loader size={15} className="animate-spin" />}
          {applying ? 'Applying…' : noChanges ? 'Import anyway' : isUpdate ? 'Apply changes' : 'Import records'}
        </button>
      </div>

      {/* Profile completeness for current rank */}
      {plan.completeness && (
        <ProfileCompletenessWidget completeness={plan.completeness} />
      )}
    </div>
  );
}

function ProfileCompletenessWidget({ completeness }: { completeness: NonNullable<import('@/lib/dg-profile/import-plan').ProfileCompleteness> }) {
  const { summary, items, tierLabel, rank } = completeness;
  const [expanded, setExpanded] = React.useState(false);

  const statusColor = summary.score >= 80 ? 'var(--status-valid)' : summary.score >= 50 ? 'var(--accent)' : 'var(--status-expired)';

  const missing = items.filter(i => i.status === 'missing' && i.critical);
  const expired = items.filter(i => i.status === 'expired');
  const expiring = items.filter(i => i.status === 'expiring_soon');

  return (
    <div className="rounded-lg border border-border bg-card">
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between px-3 py-2.5 text-left"
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-700 text-foreground">Profile completeness for {tierLabel}</span>
          {missing.length > 0 && (
            <span className="text-[10px] font-700 px-1.5 py-0.5 rounded-full" style={{ background: 'var(--status-expired-bg)', color: 'var(--status-expired)' }}>
              {missing.length} missing
            </span>
          )}
          {expired.length > 0 && (
            <span className="text-[10px] font-700 px-1.5 py-0.5 rounded-full" style={{ background: 'var(--status-expired-bg)', color: 'var(--status-expired)' }}>
              {expired.length} expired
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs font-800 tabular-nums" style={{ color: statusColor }}>{summary.score}%</span>
          <span className="text-muted-foreground text-xs">{expanded ? '▲' : '▼'}</span>
        </div>
      </button>
      {expanded && (
        <div className="border-t border-border px-3 pb-3 pt-2 space-y-1.5">
          <p className="text-[11px] text-muted-foreground mb-2">
            Required certificates for <span className="font-600 text-foreground">{rank}</span> ({summary.present} / {summary.total} present)
          </p>
          <ul className="space-y-1 max-h-48 overflow-y-auto">
            {items.map((item, idx) => {
              const icon =
                item.status === 'present' ? <CheckCircle2 size={12} style={{ color: 'var(--status-valid)' }} /> :
                item.status === 'expiring_soon' ? <Clock size={12} style={{ color: 'var(--accent)' }} /> :
                item.status === 'expired' ? <XCircle size={12} style={{ color: 'var(--status-expired)' }} /> :
                <XCircle size={12} className="text-muted-foreground" />;

              return (
                <li key={idx} className="flex items-start gap-2 text-[11px]">
                  <span className="mt-0.5 shrink-0">{icon}</span>
                  <span className={item.status === 'missing' ? 'text-muted-foreground' : 'text-foreground'}>
                    {item.label}
                    {item.status === 'expiring_soon' && item.expiresOn && (
                      <span className="text-muted-foreground ml-1">(expires {item.expiresOn})</span>
                    )}
                    {item.status === 'expired' && item.expiresOn && (
                      <span style={{ color: 'var(--status-expired)' }} className="ml-1">(expired {item.expiresOn})</span>
                    )}
                    {!item.critical && item.status === 'missing' && (
                      <span className="text-muted-foreground ml-1">(recommended)</span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
